import { Button, Tab, TabPanel, TabPanels, Tabs } from "@chakra-ui/react";
import { useFeeInformationListSuspense } from "gen/endpoints/fee-information/fee-information";
import { useFeeTimelineRetrieveSuspense } from "gen/endpoints/fee-timeline/fee-timeline";
import { useFundListSuspense } from "gen/endpoints/fund-entities/fund-entities";
import { FeeTimelineRetrieveParams } from "gen/model/feeTimelineRetrieveParams";
import { GenericTableData } from "gen/model/genericTableData";
import { GenericTableRow } from "gen/model/genericTableRow";
import { PermissionTypeEnum } from "gen/model/permissionTypeEnum";
import React, { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Restricted } from "shared/components/access/Restricted";
import EmptyState from "shared/components/empty/EmptyState";
import { TableBox } from "shared/components/table/box/TableBox";
import { TableBoxBody } from "shared/components/table/box/TableBoxBody";
import { TableBoxHeader } from "shared/components/table/box/TableBoxHeader";
import { TableBoxTabs } from "shared/components/table/box/TableBoxTabs";
import { TableBoxTitle } from "shared/components/table/box/TableBoxTitle";
import { DataTable } from "shared/components/table/data/DataTable";
import { DataTableBoxActions } from "shared/components/table/data/DataTableBoxActions";
import { GetLinkInfoForRowFn } from "shared/components/table/data/DataTableTypes";
import { Timeline } from "shared/components/timeline/Timeline";

export type FeeTableGetFeeEditPathFn = (feeId: string) => string;

export interface FeeTimelineProps {
  fundFamilyId: string;
  feeId: string;
  retrieveParams?: FeeTimelineRetrieveParams;
}

export interface FeeTableProps {
  fundFamilyId: string;
  retrieveParams?: FeeTimelineRetrieveParams;
  onAddFee?: () => void;
  getFeeEditPath: FeeTableGetFeeEditPathFn;
  showTimeline?: boolean;
}

const FeeTimeline: React.FC<FeeTimelineProps> = ({
  feeId,
  fundFamilyId,
  retrieveParams,
}) => {
  const { data: timelineData } = useFeeTimelineRetrieveSuspense(
    fundFamilyId,
    feeId,
    retrieveParams,
  );
  return <Timeline key={feeId} data={timelineData} />;
};

const FeeTable: React.FC<FeeTableProps> = ({
  fundFamilyId,
  retrieveParams,
  onAddFee,
  getFeeEditPath,
  showTimeline,
}) => {
  const { t } = useTranslation(["common", "fees", "enums"]);

  const { data: feeInformationData } = useFeeInformationListSuspense(
    fundFamilyId,
    retrieveParams ?? undefined,
  );
  const { data: fundEntitiesData } = useFundListSuspense({
    fund_family_ids: [fundFamilyId],
  });
  const fundIdToFundName = useMemo<Record<string, string>>(
    () =>
      Object.fromEntries<string>(
        fundEntitiesData.map(({ id, legal_name }) => [id, legal_name]),
      ),
    [fundEntitiesData],
  );

  const tableData = useMemo(
    (): GenericTableData => ({
      columns: [
        { id: "fee_name", title: t("fees:fee"), is_header: true },
        { id: "fee_type", title: t("common:type") },
        { id: "fee_cadence", title: t("fees:cadence") },
        { id: "fee_funds", title: t("fees:funds") },
      ],
      rows: feeInformationData.map(
        ({ fee, fund_ids }): GenericTableRow => ({
          id: fee.id,
          granularity_level: "fund_family",
          cells: {
            fee_name: {
              value: {
                type: "text",
                text: fee.name,
              },
            },
            fee_type: {
              value: {
                type: "text",
                text: t(`enums:FeeTypeEnum.${fee.fee_type}`),
              },
            },
            fee_cadence: {
              value: {
                type: "text",
                text: t(`enums:FeeCadenceEnum.${fee.cadence}`),
              },
            },
            fee_funds: {
              value: {
                type: "text",
                text:
                  fund_ids.length === 0
                    ? t("common:all")
                    : fund_ids.map((id) => fundIdToFundName[id]).join(", "),
              },
            },
          },
        }),
      ),
    }),
    [feeInformationData, fundIdToFundName, t],
  );

  const getLinkInfoForRow = useCallback<GetLinkInfoForRowFn>(
    ({ row }) =>
      row.id
        ? {
            type: "generic",
            to: getFeeEditPath(row.id),
          }
        : undefined,
    [getFeeEditPath],
  );

  const addButton = onAddFee ? (
    <Restricted permission={PermissionTypeEnum.config}>
      <Button size="xs" onClick={onAddFee}>
        {t("fees:addFee")}
      </Button>
    </Restricted>
  ) : null;

  const [tabIndex, setTabIndex] = useState(0);

  return (
    <TableBox boxProps={{ sx: { mb: "16px" } }}>
      <TableBoxHeader>
        {showTimeline ? (
          <TableBoxTabs index={tabIndex} onChange={setTabIndex}>
            <Tab>{t("fees:timeline.title")}</Tab>
            <Tab>{t("fees:list.title")}</Tab>
          </TableBoxTabs>
        ) : (
          <TableBoxTitle>{t("fees:fees")}</TableBoxTitle>
        )}
        <DataTableBoxActions createAction={addButton} />
      </TableBoxHeader>
      <TableBoxBody>
        <Tabs variant="table" index={tabIndex} isLazy={true}>
          <TabPanels>
            {showTimeline && (
              <TabPanel>
                {feeInformationData.length ? (
                  feeInformationData.map(({ fee }) => (
                    <FeeTimeline
                      key={fee.id}
                      feeId={fee.id}
                      fundFamilyId={fundFamilyId}
                      retrieveParams={retrieveParams}
                    />
                  ))
                ) : (
                  <EmptyState title={t("fees:empty.title")} />
                )}
              </TabPanel>
            )}
            <TabPanel>
              <DataTable
                data={tableData}
                getLinkInfoForRow={getLinkInfoForRow}
                emptyStateProps={{
                  title: t("fees:empty.title"),
                  children: addButton,
                }}
              />
            </TabPanel>
          </TabPanels>
        </Tabs>
      </TableBoxBody>
    </TableBox>
  );
};

export default FeeTable;
